'use client';

import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import LockIcon from '@mui/icons-material/Lock';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { Box, Button, CircularProgress, Link, Stack, Theme, Typography } from '@mui/material';
import { withClarityTheme } from '@verifime/styles';
import { ReactElement, ReactNode } from 'react';
import { Logo } from '../Logo';
import { StatusIconTile } from '../StatusIconTile/StatusIconTile';

export type ReportDownloadPhase = 'loading' | 'ready' | 'downloaded' | 'error';
export type ReportDownloadErrorKind = 'forbidden' | 'gone' | 'transient';

export type ReportDownloadPageProps = {
  phase: ReportDownloadPhase;
  /** Required when `phase === 'error'`. */
  errorKind?: ReportDownloadErrorKind;
  /** Report subject (the customer). The context line shows only when both this
   * and `organisationName` are present. The reader is the organisation's
   * compliance staff, not the customer. */
  customerName?: string;
  organisationName?: string;
  fileName?: string;
  /** Drives the ready-state note and aria-live wording. */
  autoStarted?: boolean;
  onDownload?: () => void;
  onDownloadAgain?: () => void;
  onOpenInNewTab?: () => void;
  onRetry?: () => void;
  supportEmail?: string;
  /** Defaults to the shared design-system Logo in the current colour mode. */
  logo?: ReactNode;
  'data-testid'?: string;
};

const DEFAULT_FILE_NAME = 'id-compliance-report.pdf';
const DEFAULT_SUPPORT_EMAIL = 'support@verifime.com';

function buildSupportMailto(supportEmail: string): string {
  return `mailto:${supportEmail}?subject=${encodeURIComponent('ID Compliance Report - help')}`;
}

function SupportLink({
  supportEmail,
  label,
  dataAction,
}: Readonly<{ supportEmail: string; label: string; dataAction?: string }>): ReactElement {
  return (
    <Link
      href={buildSupportMailto(supportEmail)}
      variant="body2"
      data-action={dataAction ?? 'contact-support'}
    >
      {label}
    </Link>
  );
}

function LoadingContent(): ReactElement {
  return (
    <>
      <CircularProgress />
      <Typography variant="h5" sx={{ fontWeight: (t: Theme) => t.semantic.fontWeight.semibold }}>
        Preparing your report
      </Typography>
      <Typography variant="body1" color="text.tertiary">
        Securing a one-time download link. This only takes a moment.
      </Typography>
    </>
  );
}

function ReadyContent({
  customerName,
  organisationName,
  autoStarted,
  onDownload,
  supportEmail,
}: Readonly<{
  customerName?: string;
  organisationName?: string;
  autoStarted: boolean;
  onDownload?: () => void;
  supportEmail: string;
}>): ReactElement {
  // The reader is the organisation's compliance staff; the customer is the
  // report's subject, not its recipient. Show the context line only when both
  // the subject and the organisation are known.
  const hasReportContext = Boolean(customerName && organisationName);
  const downloadAriaLabel = customerName
    ? `Download PDF, ID Compliance Report on ${customerName}`
    : 'Download PDF, ID Compliance Report';
  const readyNote = autoStarted
    ? 'Your download should start automatically. If nothing happens, select Download PDF.'
    : 'Select Download PDF to save your report.';

  return (
    <>
      <StatusIconTile
        background={(theme) => theme.palette.surface.brandSubtle}
        iconColor={(theme) => theme.palette.primary.main}
        shape="lg"
        icon={<DescriptionOutlinedIcon aria-hidden />}
      />

      <Typography variant="overline" color="primary.main">
        ID COMPLIANCE REPORT
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: (t: Theme) => t.semantic.fontWeight.semibold }}>
        Your report is ready
      </Typography>
      {hasReportContext && (
        <Typography variant="body2" color="text.tertiary">
          {`Compliance report on ${customerName}, prepared for ${organisationName}.`}
        </Typography>
      )}

      <Button
        variant="contained"
        onClick={onDownload}
        data-action="download-report"
        aria-label={downloadAriaLabel}
        startIcon={<FileDownloadOutlinedIcon aria-hidden />}
        sx={{ width: '100%' }}
      >
        Download PDF
      </Button>
      <Typography variant="body2" color="text.tertiary">
        {readyNote}
      </Typography>

      <Box
        sx={(theme: Theme) => ({ width: '100%', borderTop: `1px solid ${theme.palette.divider}` })}
      />
      <SupportLink supportEmail={supportEmail} label="Trouble downloading? Contact support" />
    </>
  );
}

function DownloadedContent({
  fileName,
  onDownloadAgain,
  onOpenInNewTab,
  supportEmail,
}: Readonly<{
  fileName: string;
  onDownloadAgain?: () => void;
  onOpenInNewTab?: () => void;
  supportEmail: string;
}>): ReactElement {
  return (
    <>
      <StatusIconTile
        background={(theme) => theme.palette.surface.successSubtle}
        iconColor={(theme) => theme.palette.success.main}
        shape="round"
        icon={<CheckCircleIcon aria-hidden />}
      />

      <Typography variant="h5" sx={{ fontWeight: (t: Theme) => t.semantic.fontWeight.semibold }}>
        Report downloaded
      </Typography>
      <Typography variant="body1" color="text.tertiary">
        Saved as{' '}
        {/* Filename in the strong default colour (per the design) so it stands
            out against the tertiary sentence: it is what the user scans for in
            their downloads / Finder. The design's optical 0.92em shrink is
            deliberately omitted to avoid a hardcoded, non-token font size. */}
        <Box component="span" sx={{ fontFamily: 'monospace', color: 'text.primary' }}>
          {fileName}
        </Box>
        . Open it in your PDF viewer, or get it again below.
      </Typography>

      <Stack sx={(theme: Theme) => ({ width: '100%', gap: theme.semantic.spacing.sm })}>
        <Button
          variant="outlined"
          onClick={onDownloadAgain}
          data-action="download-report-again"
          startIcon={<FileDownloadOutlinedIcon aria-hidden />}
          sx={{ width: '100%' }}
        >
          Download again
        </Button>
        <Button
          variant="text"
          onClick={onOpenInNewTab}
          data-action="open-report-new-tab"
          startIcon={<OpenInNewIcon aria-hidden />}
          sx={{ width: '100%' }}
        >
          Open in new tab
        </Button>
      </Stack>

      <SupportLink supportEmail={supportEmail} label="Trouble opening it? Contact support" />
    </>
  );
}

// VME-4162: wording supplied by the legal team via product. Copy it verbatim; do not reword.
const CONFIDENTIALITY_NOTICE_TITLE = 'Strictly Confidential - Restricted AML/CTF Information';
const CONFIDENTIALITY_NOTICE_BODY =
  'This report has been prepared exclusively for the purpose of the generating organisation’s customer due diligence requirements. It contains information from the organisation’s AML/CTF system that is confidential and sensitive. This report must not be disclosed, distributed, copied, forwarded, reproduced or otherwise made available to any person outside the organisation unless the disclosure is authorised by the organisation and is permitted by applicable law, where appropriate consent has been obtained by the relevant entity being identified and where the receiving party has a reliance arrangement in place that complies with the Anti-Money Laundering and Counter-Terrorism Financing Act 2006 (Cth) and is compliant with the requirements of the Privacy Act 1988 (Cth).';

/** Shown below the card whenever a report is in hand (ready and downloaded). The lock icon sits
 * inside the title, ahead of its text, so on a narrow screen it stays on the title's first line
 * instead of becoming a column beside a wrapped title. */
function ConfidentialityNotice(): ReactElement {
  return (
    <Stack
      sx={(theme: Theme) => ({
        width: '100%',
        maxWidth: ['100%', theme.semantic.size.sm],
        paddingX: theme.semantic.spacing.base,
        gap: theme.semantic.spacing.xs,
        textAlign: 'center',
        boxSizing: 'border-box',
      })}
    >
      <Typography
        component="p"
        color="text.primary"
        sx={(theme: Theme) => ({
          fontSize: theme.semantic.fontSize.xs,
          fontWeight: theme.semantic.fontWeight.semibold,
          lineHeight: theme.semantic.lineHeight.short,
        })}
      >
        <LockIcon
          aria-hidden
          sx={(theme: Theme) => ({
            fontSize: theme.semantic.size['8xs'],
            verticalAlign: 'text-bottom',
            marginRight: theme.semantic.spacing.xs,
          })}
        />
        {CONFIDENTIALITY_NOTICE_TITLE}
      </Typography>
      <Typography
        component="p"
        color="text.tertiary"
        sx={(theme: Theme) => ({
          fontSize: theme.semantic.fontSize.xs,
          lineHeight: theme.semantic.lineHeight.long,
        })}
      >
        {CONFIDENTIALITY_NOTICE_BODY}
      </Typography>
    </Stack>
  );
}

type ErrorCopy = {
  heading: string;
  body: string;
  tile: { background: (theme: Theme) => string; iconColor: (theme: Theme) => string; icon: ReactElement };
};

const ERROR_COPY: Record<ReportDownloadErrorKind, ErrorCopy> = {
  forbidden: {
    heading: "You don't have access to this report",
    body: 'Your access may have been removed, or this link was issued to a different person. Ask your VerifiMe administrator, or contact support.',
    tile: {
      background: (theme) => theme.palette.surface.dangerSubtle,
      iconColor: (theme) => theme.palette.error.main,
      icon: <BlockIcon aria-hidden />,
    },
  },
  gone: {
    heading: 'This report is no longer available',
    body: 'The report may have been removed, or this link has expired. You can re-generate it from the VerifiMe portal, or contact support for help.',
    tile: {
      background: (theme) => theme.palette.surface.neutralSubtle,
      iconColor: (theme) => theme.palette.text.tertiary,
      icon: <ScheduleIcon aria-hidden />,
    },
  },
  transient: {
    heading: 'Something went wrong',
    body: "We couldn't prepare your download just now. This is usually temporary. Please try again.",
    tile: {
      background: (theme) => theme.palette.surface.warningSubtle,
      iconColor: (theme) => theme.palette.warning.main,
      icon: <ErrorOutlineIcon aria-hidden />,
    },
  },
};

function ErrorContent({
  errorKind,
  onRetry,
  supportEmail,
}: Readonly<{
  errorKind: ReportDownloadErrorKind;
  onRetry?: () => void;
  supportEmail: string;
}>): ReactElement {
  const copy = ERROR_COPY[errorKind];

  return (
    <>
      <StatusIconTile
        background={copy.tile.background}
        iconColor={copy.tile.iconColor}
        shape="round"
        icon={copy.tile.icon}
      />

      <Typography variant="h5" sx={{ fontWeight: (t: Theme) => t.semantic.fontWeight.semibold }}>
        {copy.heading}
      </Typography>
      <Typography variant="body1" color="text.tertiary">
        {copy.body}
      </Typography>

      {errorKind === 'transient' ? (
        <>
          <Button
            variant="contained"
            onClick={onRetry}
            data-action="retry-report-download"
            startIcon={<RefreshIcon aria-hidden />}
            sx={{ width: '100%' }}
          >
            Try again
          </Button>
          <Typography variant="body2" color="text.tertiary">
            Try again re-requests the file: it won&apos;t reload the page.
          </Typography>
          <SupportLink supportEmail={supportEmail} label="Still stuck? Contact support" />
        </>
      ) : (
        <Button
          variant="outlined"
          component="a"
          href={buildSupportMailto(supportEmail)}
          data-action="contact-support"
          sx={{ width: '100%' }}
        >
          Contact support
        </Button>
      )}
    </>
  );
}

function announcementFor(props: Readonly<ReportDownloadPageProps>, fileName: string): string {
  switch (props.phase) {
    case 'loading':
      return 'Preparing your report: securing a one-time download link.';
    case 'ready':
      return props.autoStarted
        ? 'Your report is ready. Your download has started automatically.'
        : 'Your report is ready. Select Download PDF to save it.';
    case 'downloaded':
      return `Report downloaded: ${fileName} saved.`;
    case 'error':
      switch (props.errorKind) {
        case 'forbidden':
          return "Error: you don't have access to this report.";
        case 'gone':
          return 'Error: this report is no longer available.';
        case 'transient':
        default:
          return 'Error: something went wrong preparing your download.';
      }
  }
}

function ReportDownloadPageComponent(props: Readonly<ReportDownloadPageProps>): ReactElement {
  const {
    phase,
    errorKind,
    customerName,
    organisationName,
    fileName = DEFAULT_FILE_NAME,
    autoStarted = false,
    onDownload,
    onDownloadAgain,
    onOpenInNewTab,
    onRetry,
    supportEmail = DEFAULT_SUPPORT_EMAIL,
    logo,
    'data-testid': dataTestId = 'report-download-page',
  } = props;
  const logoNode = logo ?? <Logo colorMode="auto" layout="horizontal" size="medium" />;

  let content: ReactElement;
  switch (phase) {
    case 'loading':
      content = <LoadingContent />;
      break;
    case 'ready':
      content = (
        <ReadyContent
          customerName={customerName}
          organisationName={organisationName}
          autoStarted={autoStarted}
          onDownload={onDownload}
          supportEmail={supportEmail}
        />
      );
      break;
    case 'downloaded':
      content = (
        <DownloadedContent
          fileName={fileName}
          onDownloadAgain={onDownloadAgain}
          onOpenInNewTab={onOpenInNewTab}
          supportEmail={supportEmail}
        />
      );
      break;
    case 'error':
    default:
      content = (
        <ErrorContent
          // errorKind is required by contract when phase === 'error'; 'transient'
          // is a defensive fallback if a caller omits it.
          errorKind={errorKind ?? 'transient'}
          onRetry={onRetry}
          supportEmail={supportEmail}
        />
      );
      break;
  }

  return (
    <Stack
      data-testid={dataTestId}
      sx={(theme: Theme) => ({
        minHeight: '100vh',
        width: '100%',
        backgroundColor: theme.palette.background.default,
        padding: theme.semantic.spacing.lg,
        boxSizing: 'border-box',
      })}
    >
      {/* Logo as page chrome: centred at the top on mobile, top-left on desktop. */}
      <Box sx={{ display: 'flex', width: '100%', justifyContent: ['center', 'flex-start'] }}>
        {logoNode}
      </Box>

      {/* Centre the screen in the space left below the logo. */}
      <Stack
        sx={(theme: Theme) => ({
          flex: 1,
          width: '100%',
          alignItems: 'center',
          justifyContent: 'center',
          gap: theme.semantic.spacing['2lg'],
        })}
      >
        <Stack
          sx={(theme: Theme) => ({
            width: '100%',
            maxWidth: ['100%', theme.semantic.size.sm],
            alignItems: 'center',
            textAlign: 'center',
            gap: theme.semantic.spacing.base,
            boxSizing: 'border-box',
            backgroundColor: ['transparent', theme.palette.background.paper],
            boxShadow: ['none', theme.shadows[2]],
            borderRadius: [0, theme.semantic.borderRadius.lg],
            padding: [0, theme.semantic.spacing['3xl']],
            paddingBottom: [0, theme.semantic.spacing['2xl']],
          })}
        >
          {content}
        </Stack>
        {(phase === 'ready' || phase === 'downloaded') && <ConfidentialityNotice />}
      </Stack>

      {/* Visually-hidden live region: announces the current state to screen
          reader users without stealing focus (this is a status update, not an
          alert takeover like ErrorBoundaryScreen). */}
      <Box
        role="status"
        aria-live="polite"
        sx={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        {announcementFor(props, fileName)}
      </Box>
    </Stack>
  );
}

export const ReportDownloadPage = withClarityTheme(ReportDownloadPageComponent);
